import { Badge, cn, Select, StatusBadge, Text } from '@pcrm/fe/ui-kit';
import { formatDateTime } from '../../lib/format';
import {
  DOCUMENT_SOURCE,
  DOCUMENT_STATE,
  type DocumentListItem,
  documentStateDescriptor,
} from './documents.api';

/**
 * Cum se vede în listă un document scos din ea — arhivat el însuși, sau al unui **client
 * arhivat**.
 *
 * ## Rândul întreg, tăiat cu o linie
 *
 * A fost întâi o a doua insignă, „Arhivat", lângă stare. Fondatorul a cerut altfel
 * (2026-09-17): *„în loc să apară arhivat scris… hai să tăiem cu o linie tot rândul"*. Și e
 * mai bine: insigna cerea citită o etichetă în plus pe fiecare rând, într-o coloană unde
 * ochiul caută starea actului. Linia se vede din periferie, fără să fie citită, și nu
 * ocupă loc.
 *
 * ⚠️ **Linia spune „nu mai contează", nu spune „client arhivat".** De aceea rândul poartă și
 * un `title`: la hover se află motivul și data. Fără el, singurul loc din care s-ar fi putut
 * afla ar fi fost comutatorul din capul listei — iar în tabul de pe fișa clientului nici
 * acela nu există.
 *
 * ## De ce nu e o stare a documentului
 *
 * Arhivarea e a **clientului**. Actul își păstrează starea lui — `Semnat` rămâne `Semnat`,
 * semnătura e dovada — iar `customerArchivedAt` vine din `crm_customers.archived_at`, nu
 * dintr-o coloană copiată pe document: copia ar fi rămas pusă după dezarhivare, care nu
 * desface nimic ([#111](https://github.com/bplugaru/efx-pcrm/issues/111)).
 * Vezi docs/wiki/domains/document-manager.md#documentele-unui-client-arhivat
 *
 * ## Și documentul se poate arhiva
 *
 * De la [#141](https://github.com/bplugaru/efx-pcrm/issues/141), un act **anulat** se poate
 * arhiva la rândul lui — `crm_documents.archived_at`, de data asta chiar o coloană a
 * documentului. Rândul primește aceeași linie, pentru că înseamnă același lucru pentru ochi:
 * „e aici doar pentru că l-ai cerut". Ce se deosebește e motivul, iar motivul stă în `title`.
 */

/**
 * Ce se pune pe `<Table.Row>` pentru documentul unui client arhivat.
 *
 * Funcție, nu două clase copiate în două ecrane: registrul „Documente emise" și tabul
 * „Documente" de pe fișa clientului desenează același rând. Prima dată, insigna a intrat în
 * amândouă doar pentru că am umblat la amândouă în aceeași oră — genul de nepotrivire pe
 * care nimeni n-o caută, pentru că fiecare ecran în parte arată corect.
 *
 * Se întinde cu `...archivedRowProps(document)`, **după** `className`-ul propriu al
 * ecranului, care se dă prin `extraClassName`: altfel al doilea `className` l-ar fi
 * suprascris pe primul, iar rândurile din registru ar fi pierdut `cursor-pointer`.
 */
export function archivedRowProps(
  document: DocumentListItem,
  extraClassName?: string,
): { className: string; title?: string } {
  /*
    Două arhivări, același semn pe rând.

    Actul arhivat (#141) e ascuns din liste, ca și cel al unui client arhivat, și se vede doar
    când filtrul îl cere anume — deci ochiul are nevoie de aceeași linie, din același motiv.
    Nu se cumulează vizual: un rând nu poate fi tăiat de două ori, iar o a doua nuanță ar fi
    cerut citită o deosebire pe care operatorul n-o folosește la nimic.

    ⚠️ **Deosebirea rămâne în `title`.** „De ce e tăiat rândul ăsta" are două răspunsuri
    diferite — actul e arhivat, sau clientul lui e — iar când sunt amândouă adevărate se scriu
    amândouă: actul iese din arhivă singur, clientul nu-l scoate.
  */
  const reasons = [
    document.archivedAt === null
      ? null
      : `Documentul e arhivat de la ${formatDateTime(document.archivedAt)}. Numărul rămâne consumat.`,
    document.customerArchivedAt === null
      ? null
      : `Clientul e arhivat de la ${formatDateTime(document.customerArchivedAt)}. Actul rămâne în registru.`,
  ].filter((reason): reason is string => reason !== null);

  return {
    className: cn(extraClassName, reasons.length > 0 && 'line-through'),
    title: reasons.length === 0 ? undefined : reasons.join(' '),
  };
}

/**
 * Filtrul documentelor arhivate: **trei valori**, nu un comutator.
 *
 * Arhivarea clientului are comutator („Arată și clienții arhivați") pentru că întrebarea e
 * una singură. Aici sunt două, și a doua e chiar drumul de întoarcere: „unde e actul pe care
 * l-am arhivat ieri" se răspunde cu **doar arhivate**, iar cu un comutator ar fi însemnat să
 * cauți rândul tăiat printre toate celelalte. Aceleași trei valori ca pe server
 * ([#141](https://github.com/bplugaru/efx-pcrm/issues/141)).
 *
 * Un singur component pentru amândouă listele — registrul „Documente emise" și tabul
 * „Documente" de pe fișa clientului — din același motiv ca `archivedRowProps`: două copii
 * ar fi divergat la prima valoare adăugată, iar fiecare ecran în parte ar fi arătat corect.
 */
export function ArchivedDocumentsFilter({
  id,
  value,
  onChange,
}: {
  id: string;
  value: 'all' | 'only' | undefined;
  onChange: (value: 'all' | 'only' | undefined) => void;
}) {
  return (
    <Select
      value={value ?? NO_ARCHIVED}
      onValueChange={(next) =>
        onChange(next === NO_ARCHIVED ? undefined : (next as 'all' | 'only'))
      }
    >
      <Select.Trigger id={id} className="w-44">
        <Select.Value />
      </Select.Trigger>
      <Select.Content className="z-50">
        {/* „Fără arhivate", nu „Toate": implicitul chiar ascunde ceva, iar un „Toate" care
            ascunde e exact eticheta care face rândul lipsă să pară o defecțiune. */}
        <Select.Item value={NO_ARCHIVED}>Fără arhivate</Select.Item>
        <Select.Item value="all">Și arhivate</Select.Item>
        <Select.Item value="only">Doar arhivate</Select.Item>
      </Select.Content>
    </Select>
  );
}

/** `Select` nu acceptă valoare goală, iar „fără filtru" e chiar implicitul. */
const NO_ARCHIVED = '_fara_arhivate';

/**
 * **Numărul din registru, sau lipsa lui** ([#142](https://github.com/bplugaru/efx-pcrm/issues/142)).
 *
 * O ciornă n-are număr: emiterea e trimiterea la semnat (#221). Se scrie „—", nu se lasă gol — o celulă vidă
 * într-un tabel se citește ca „nu s-a încărcat", iar ce trebuie citit aici e „încă n-a fost
 * emis". Ce spune de ce e insigna de stare de alături, care scrie „Ciornă".
 *
 * ⚠️ **Nu scrie „Ciornă" și aici.** Ar fi al doilea cuvânt cu același înțeles pe același
 * rând, exact greșeala evitată la insigna „Primit" — vezi `DocumentStateCell`.
 */
export function DocumentNumberCell({ document }: { document: DocumentListItem }) {
  return document.documentNumber === null ? (
    <span className="text-ui-fg-muted">—</span>
  ) : (
    <span>{document.documentNumber}</span>
  );
}

/**
 * Starea documentului, cu motivul anulării dedesubt.
 *
 * ⚠️ **Un act cu număr, în `1`, scrie „Nesemnat", oricum ar fi ajuns acolo**
 * ([#221](https://github.com/bplugaru/efx-pcrm/issues/221)). Doi pași îl aduc: încărcarea unui
 * act primit, și un link de semnare mort — invalidat, expirat, sau clientul arhivat. Al doilea
 * se întoarce în `1` cu numărul alocat la trimitere: a plecat la client, e în registrul de acte
 * emise, iar „Ciornă" ar spune că nu l-a văzut nimeni.
 *
 * ⚠️ **Un act primit nu scrie „Ciornă".** Starea `1` înseamnă „există, fără semnătură"; pe un
 * act emis de noi asta e o **ciornă** (#142), pe unul venit din afară e pur și simplu
 * **nesemnat** — el are număr din clipa încărcării, deci a fost emis.
 * Scria „Generat" despre un PDF scanat pe care nu l-am generat niciodată — fondatorul,
 * 2026-09-18: *„stare primit (nesemnat) se duce în registru documente care are starea
 * generat. este ok asta?"*. Nu era ([#150](https://github.com/bplugaru/efx-pcrm/issues/150)).
 *
 * **Fără o stare nouă în bază.** Perechea `source` + `document_state` o spunea deja; doar
 * ecranul n-o citea. Și fără să repete insigna „Primit" de lângă tip: aia spune **ce fel de
 * act e**, asta spune **ce s-a întâmplat cu el**.
 */
export function DocumentStateCell({ document }: { document: DocumentListItem }) {
  const state = documentStateDescriptor(document.documentState);
  // Numărul spune că actul a fost emis; `1` spune că n-are semnătură. Vezi `isDraft`.
  const unsigned =
    document.documentNumber !== null && document.documentState === DOCUMENT_STATE.draft;

  return (
    <div className="flex flex-col items-start gap-y-1">
      <StatusBadge color={state.color}>{unsigned ? 'Nesemnat' : state.label}</StatusBadge>
      {document.cancelReason ? (
        <Text size="small" className="text-ui-fg-muted">
          {document.cancelReason}
        </Text>
      ) : null}
    </div>
  );
}

/**
 * Semnul că actul **a venit din afară**, nu l-am emis noi.
 *
 * Definiția de gata a [#108](https://github.com/bplugaru/efx-pcrm/issues/108) o cere
 * explicit: actul urcat „apare în tabul Documente și în registru, **marcat «încărcat»**".
 *
 * ⚠️ **Scrie „Primit", nu „Încărcat".** `crm_documents.source` are două valori, nu trei:
 * `generat` și `primit`. Un act urcat de mână și unul atribuit din căsuța de email sunt
 * același lucru pentru registru — au bytes în spate, nu text — iar decizia e a lui #109, cu
 * comentariul din `document-source.ts` scris exact pentru cazul ăsta. O a treia valoare ar
 * fi însemnat o migrare și o distincție pe care niciun ecran n-o folosește; de unde a venit
 * fișierul se citește din jurnal și din „Documente în așteptare".
 *
 * **Lângă tip, nu lângă stare.** Starea spune ce s-a întâmplat cu actul, proveniența spune
 * ce fel de act e — iar a doua insignă în coloana stării ar fi făcut exact ce a fost scos la
 * arhivare: o etichetă în plus de citit acolo unde ochiul caută altceva.
 */
export function DocumentSourceMark({ document }: { document: DocumentListItem }) {
  if (document.source !== DOCUMENT_SOURCE.received) {
    return null;
  }

  return (
    <Badge size="2xsmall" color="orange" title="Act primit din afară, nu generat de noi.">
      Primit
    </Badge>
  );
}
